import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type {
  PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, RenderTask, TextLayer,
} from "pdfjs-dist/legacy/build/pdf.mjs";
import { loadPdfjs } from "./pdfjs.js";
// Blatt-, Leinwand- und Textebenenregeln gehören zum Baustein — auch wo keine Belegvorschau
// sie mitbringt (Versionspanel, Review M-7).
import "./Belegvorschau.css";

/**
 * Die Blätter eines PDF, Seite für Seite (Entscheid 799, 04.11.2026): pdf.js zeichnet jede
 * Seite auf eine Leinwand in Gerätedichte, darüber liegt die Textebene (markierbar).
 *
 * **Die Grösse jedes Blattes ist reine CSS-Rechnung**, bevor ein Pixel gezeichnet ist: Der
 * Rahmen trägt `--blatt-breite` (Breite bei 100 % in CSS-Pixeln) und `--blatt-verhaeltnis`
 * (Breite / Höhe); die Zoom-Regeln in Belegvorschau.css machen daraus die Breite, das
 * Verhältnis die Höhe. Nichts springt, der Seitenzähler rechnet mit echten Höhen.
 *
 * **Gezeichnet wird faul**: Ein Blatt wird gezeichnet, sobald es in den Rand um das
 * Sichtfenster kommt (IntersectionObserver, 150 % der Höhe), und wieder freigegeben, sobald
 * es ihn verlässt — so sind nie mehr als rund vier Sichtfenster an Leinwänden im Speicher,
 * gleich ob der Beleg 3 oder 300 Seiten hat. Ohne Observer (jsdom) wird sofort gezeichnet.
 *
 * **Neu gezeichnet** wird bei geänderter Blattbreite (Zoom, Fenster), entprellt; ein
 * laufender Zeichenauftrag wird abgebrochen. Die Pufferbreite ist bei MAX_BUFFER_WIDTH
 * gekappt (iOS begrenzt Leinwände auf rund 16,7 Millionen Punkte).
 *
 * Der Baustein kennt KEINE Belegart und keinen Klienten (wie die Belegvorschau selbst).
 *
 * Folgepunkt (Review M-4, bewusst nicht gebaut): Ein Wechsel der Gerätedichte ohne
 * Breitenänderung (Fenster auf einen anderen Bildschirm gezogen, Pinch-Zoom) zeichnet nicht
 * neu — das Blatt bleibt bis zum nächsten Anlass in der alten Dichte.
 */
export type PdfPagesProps = {
  /** Das PDF als Base64. Eine neue Zeichenkette ist ein neues Dokument. */
  pdf: string;
  /** Sobald das Dokument geöffnet und alle Seitenmasse bekannt sind. */
  onOpened?: ((d: { pages: number }) => void) | undefined;
  /** Wenn pdf.js das Dokument nicht öffnen kann. */
  onFailed?: ((error: unknown) => void) | undefined;
  /**
   * Der Kasten, in dem die Blätter rollen — Wurzel des faulen Zeichnens (IntersectionObserver).
   * Ohne Angabe der nächste `.bvor__seiten` über dem Blatt (Belegvorschau). Wer anders rollt
   * (Versionspanel: der Inhalt von me-dialog liegt im Schatten-DOM), gibt seinen Kasten an.
   */
  scrollRoot?: Element | null | undefined;
};

/** Grösste Pufferbreite einer Leinwand in Gerätepixeln (Spec: 3200, iOS-Grenze). */
export const MAX_BUFFER_WIDTH = 3200;
/** CSS-Pixel je PDF-Punkt (96 dpi gegen 72 pt je Zoll). */
const CSS_PER_POINT = 96 / 72;
/** Entprellung des Neuzeichnens nach Grössenänderung. */
const RESIZE_DELAY_MS = 150;

type PageSize = { width: number; height: number };
type OpenDocument = { proxy: PDFDocumentProxy; sizes: PageSize[] };

function bytesFromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Der Massstab der Textebene (`--total-scale-factor`): CSS-Pixel je PDF-Punkt bei der
 * gezeigten Blattbreite. Er folgt der CSS-Breite, nicht dem Puffer — steht der Puffer an
 * der Kappe, ändert sich nur er (Fixrunde 1, I-1).
 */
export const textScaleFor = (cssWidth: number, baseWidth: number): string => String(cssWidth / baseWidth);

export function PdfPages(p: PdfPagesProps): ReactElement {
  const [doc, setDoc] = useState<OpenDocument | null>(null);
  const onOpenedRef = useRef(p.onOpened);
  onOpenedRef.current = p.onOpened;
  const onFailedRef = useRef(p.onFailed);
  onFailedRef.current = p.onFailed;
  /* Das zuletzt geöffnete Dokument — wird beim Wechsel erst zerstört, wenn das neue steht
     (die alten Blätter bleiben solange sichtbar), und beim Aushängen. Gehalten wird der
     Ladeauftrag: Seit pdf.js 6 zerstört nur er das Dokument (samt Worker-Anteil). */
  const current = useRef<PDFDocumentLoadingTask | null>(null);

  useEffect(() => {
    let alive = true;
    /* Der Ladeauftrag, solange er noch nicht das gezeigte Dokument ist: Ihn bricht die
       Aufräumfunktion ab (Aushängen oder neues PDF mitten im Öffnen, Review M-3). */
    let pending: PDFDocumentLoadingTask | null = null;
    void (async () => {
      try {
        const pdfjs = await loadPdfjs();
        if (!alive) return;
        pending = pdfjs.getDocument({ data: bytesFromBase64(p.pdf) });
        const loading = pending;
        const proxy = await loading.promise;
        const sizes: PageSize[] = [];
        for (let n = 1; n <= proxy.numPages; n++) {
          const page = await proxy.getPage(n);
          const vp = page.getViewport({ scale: CSS_PER_POINT });
          sizes.push({ width: round2(vp.width), height: round2(vp.height) });
        }
        if (!alive) return; // die Aufräumfunktion hat den Auftrag schon zerstört
        pending = null;
        void current.current?.destroy();
        current.current = loading;
        setDoc({ proxy, sizes });
        onOpenedRef.current?.({ pages: sizes.length });
      } catch (e) {
        if (!alive) return;
        void pending?.destroy();
        pending = null;
        // Das NEUE Dokument ist unlesbar: Die alten Blätter dürfen nicht stehen bleiben, als
        // zeigten sie es (Review M-1).
        void current.current?.destroy();
        current.current = null;
        setDoc(null);
        onFailedRef.current?.(e);
      }
    })();
    return () => {
      alive = false;
      void pending?.destroy();
      pending = null;
    };
  }, [p.pdf]);

  useEffect(() => () => {
    void current.current?.destroy();
    current.current = null;
  }, []);

  if (doc === null) return <></>;
  return (
    <>
      {doc.sizes.map((size, i) => (
        <PdfPage key={i + 1} number={i + 1} proxy={doc.proxy} size={size} scrollRoot={p.scrollRoot} />
      ))}
    </>
  );
}

function PdfPage(p: {
  number: number; proxy: PDFDocumentProxy; size: PageSize; scrollRoot: Element | null | undefined;
}): ReactElement {
  const sheet = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const text = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sheet.current, cv = canvas.current, tx = text.current;
    if (el === null || cv === null || tx === null) return;
    let gone = false;
    let visible = false;
    let page: PDFPageProxy | null = null;
    let task: RenderTask | null = null;
    let textLayer: TextLayer | null = null;
    let drawnWidth = 0;
    /** CSS-Breite und Seitenbreite in Punkten der letzten Zeichnung (für den Massstab der Textebene). */
    let drawnCssWidth = 0;
    let baseWidth = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const release = (): void => {
      task?.cancel();
      task = null;
      // Seitenobjekte (Bilder, Schriften der Seite) freigeben (Review M-2). Im try: Beim Dokumentwechsel
      // kann das Dokument schon zerstört sein, bevor dieses Blatt aufräumt — ein Wurf hier bräche Reacts Abbau.
      try { page?.cleanup(); } catch { /* Dokument schon zerstört */ }
      textLayer?.cancel();
      textLayer = null;
      cv.width = 0;
      cv.height = 0;
      tx.replaceChildren();
      drawnWidth = 0;
    };

    const draw = async (): Promise<void> => {
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = el.clientWidth || p.size.width;
      const bufferWidth = Math.min(Math.round(cssWidth * dpr), MAX_BUFFER_WIDTH);
      if (bufferWidth === drawnWidth) {
        // Gleicher Puffer (Kappe erreicht), andere Blattbreite: Die Leinwand skaliert per CSS
        // mit, nur die Textebene braucht den neuen Massstab — kein Neuzeichnen.
        if (cssWidth !== drawnCssWidth && baseWidth > 0) {
          tx.style.setProperty("--total-scale-factor", textScaleFor(cssWidth, baseWidth));
          drawnCssWidth = cssWidth;
        }
        return;
      }
      const pdfjs = await loadPdfjs();
      page ??= await p.proxy.getPage(p.number);
      if (gone || !visible) return;
      task?.cancel();
      baseWidth = page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale: bufferWidth / baseWidth });
      cv.width = Math.round(viewport.width);
      cv.height = Math.round(viewport.height);
      drawnWidth = bufferWidth;
      drawnCssWidth = cssWidth;
      // pdf.js 6.5 holt sich den Zeichenkontext selbst (`canvas` statt des alten `canvasContext`);
      // ohne `GlobalWorkerOptions.rendererSrc` zeichnet es im Hauptfaden, die Leinwand bleibt unsere.
      task = page.render({ canvas: cv, viewport });
      try {
        await task.promise;
        task = null; // fertig: `release` bricht keinen abgeschlossenen Auftrag mehr ab
      } catch (e) {
        if (e instanceof pdfjs.RenderingCancelledException) return;
        drawnWidth = 0; // echter Fehler: der nächste Anlass zeichnet neu
        throw e;
      }
      if (gone) return;
      // Die Textebene in CSS-Massstab über der Leinwand — neu aufgebaut je Zeichnung. Den
      // Massstab (`--total-scale-factor`, CSS-Pixel je PDF-Punkt) setzt im Betrachter von
      // pdf.js die Seite; hier gibt es keine, also setzen wir ihn an der Ebene selbst. Ihre
      // Masse setzt der TextLayer (setLayerDimensions) — siehe `.bvor__text` in der CSS.
      // Gedrehte Seiten (`/Rotate`) behandelt die Ebene nicht (keine `data-main-rotation`-
      // Regeln): Die PDFs der Engine sind nie gedreht (Review Task 3, M-2).
      textLayer?.cancel();
      tx.replaceChildren();
      const cssViewport = page.getViewport({ scale: cssWidth / baseWidth });
      tx.style.setProperty("--total-scale-factor", textScaleFor(cssWidth, baseWidth));
      textLayer = new pdfjs.TextLayer({
        textContentSource: page.streamTextContent(), container: tx, viewport: cssViewport,
      });
      await textLayer.render();
    };
    // Ein Zeichenfehler lässt das Blatt Papier bleiben — gemeldet wird er auf der Konsole (Review M-5).
    const drawSafely = (): void => {
      void draw().catch((e: unknown) => { console.warn(`Belegvorschau: Seite ${p.number} liess sich nicht zeichnen.`, e); });
    };

    const root = p.scrollRoot ?? el.closest(".bvor__seiten");
    const io = typeof IntersectionObserver === "undefined" ? null
      : new IntersectionObserver((entries) => {
          visible = entries.some((e) => e.isIntersecting);
          if (visible) drawSafely(); else release();
        }, { root, rootMargin: "150% 0px" });
    if (io !== null) io.observe(el); else { visible = true; drawSafely(); }

    const ro = typeof ResizeObserver === "undefined" ? null
      : new ResizeObserver(() => {
          if (!visible) return;
          if (timer !== null) clearTimeout(timer);
          timer = setTimeout(drawSafely, RESIZE_DELAY_MS);
        });
    ro?.observe(el);

    return () => {
      gone = true;
      io?.disconnect();
      ro?.disconnect();
      if (timer !== null) clearTimeout(timer);
      release();
    };
  }, [p.proxy, p.number, p.size.width, p.scrollRoot]);

  return (
    <figure className="bvor__blattrahmen" data-seite={p.number}
      style={{ "--blatt-breite": `${p.size.width}px`,
               "--blatt-verhaeltnis": `${p.size.width} / ${p.size.height}` } as CSSProperties}>
      <div className="bvor__blatt" role="img" aria-label={`Seite ${p.number}`} ref={sheet}>
        <canvas className="bvor__leinwand" ref={canvas} />
        <div className="bvor__text textLayer" ref={text} />
      </div>
      <figcaption className="bvor__blattnummer">{`Seite ${p.number}`}</figcaption>
    </figure>
  );
}
